/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "hooks/useSnackbar";
import { useParams } from "react-router-dom";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import {
  getSingleQueueStats,
  getQueueJobs,
  getJobDetail,
  retryJob,
  removeJob,
} from "services/queueService";
import {
  QueueName,
  QueueStats,
  JobState,
  BaseJob,
  FailedJob,
  DelayedJob,
  JobDetail,
  QUEUE_LABELS,
  JOB_STATE_COLORS,
} from "types/queue";
import JobsTable from "./components/JobsTable";
import JobDetailModal from "./components/JobDetailModal";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";

const useStyles = makeStyles((theme: any) => ({
  pageContainer: {
    padding: "12px",
  },
  headerSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 24,
    color: "#1d1030",
  },
  headerSubtitle: {
    fontSize: 13,
    color: "#666",
    marginTop: 4,
  },
  headerActions: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  refreshButton: {
    minWidth: 40,
    padding: "8px !important",
  },
  lastUpdated: {
    fontSize: 12,
    color: "#666",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  statsBar: {
    display: "flex",
    gap: 16,
    marginBottom: 24,
    padding: "16px 20px",
    backgroundColor: "#f8f9fa",
    borderRadius: 12,
    flexWrap: "wrap",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  statDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
  statLabel: {
    fontSize: 13,
    color: "#666",
  },
  statValue: {
    fontWeight: 700,
    fontSize: 14,
  },
  tabsContainer: {
    marginBottom: 20,
  },
  tab: {
    textTransform: "none",
    fontWeight: 600,
    minHeight: 48,
  },
  filterSection: {
    display: "flex",
    gap: 16,
    marginBottom: 20,
    alignItems: "center",
  },
  filterControl: {
    minWidth: 150,
  },
  tableContainer: {
    marginTop: 16,
  },
  noData: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#666",
    backgroundColor: "#f9f9f9",
    borderRadius: 12,
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface QueueJobsProps {
  queueType: "rgs" | "cms";
}

const QueueJobs: React.FC<QueueJobsProps> = ({ queueType }) => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  const queueName: QueueName = queueType === "rgs" ? "rgs-queue" : "cms-queue";

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [jobs, setJobs] = useState<BaseJob[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Job detail modal
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);

  // Delete consent
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  // Permissions
  const hasRetryPermission = sessionHelper.hasRole(
    PermittedRoles.QUEUE_MONITORING.RETRY_ROLES
  );
  const hasAdminPermission = sessionHelper.hasRole(
    PermittedRoles.QUEUE_MONITORING.ADMIN_ROLES
  );

  // Map tab index to job state
  const tabStates: JobState[] = [
    "failed",
    "delayed",
    "waiting",
    "active",
    "completed",
  ];

  const getStateFromTab = (tabIndex: number): JobState => {
    return tabStates[tabIndex];
  };

  // Fetch data
  const fetchData = useCallback(
    async (showRefreshing = false) => {
      try {
        if (showRefreshing) setRefreshing(true);
        else setLoading(true);

        const [statsResp, jobsResp] = await Promise.all([
          getSingleQueueStats(queueName),
          getQueueJobs({
            queueName,
            state: getStateFromTab(activeTab),
            limit: 100,
          }),
        ]);

        if (statsResp?.success || statsResp?.status) {
          setStats(statsResp.data.stats);
        }
        if (jobsResp?.success || jobsResp?.status) {
          setJobs(jobsResp.data.jobs || []);
        }
        setLastUpdated(new Date());
      } catch (error: any) {
        showSnackbar(error.message || "Failed to fetch queue data", "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [queueName, activeTab]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handle view job details
  const handleViewDetails = async (job: BaseJob) => {
    try {
      setActionLoading(true);
      const resp = await getJobDetail(queueName, job.id);
      if (resp?.success || resp?.status) {
        setSelectedJob(resp.data);
        setJobDetailOpen(true);
      }
    } catch (error: any) {
      showSnackbar(error.message || "Failed to fetch job details", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle retry job
  const handleRetryJob = async (job: BaseJob) => {
    try {
      setActionLoading(true);
      const resp = await retryJob(queueName, job.id);
      if (resp?.success || resp?.status) {
        showSnackbar("Job queued for retry", "success");
        fetchData(true);
      }
    } catch (error: any) {
      showSnackbar(error.message || "Failed to retry job", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle remove job - open consent
  const handleRemoveJobClick = (job: BaseJob) => {
    updateDetails({
      id: job.id,
      name: job.name,
    });
    openConsent();
  };

  // Confirm remove job
  const handleConfirmRemove = async () => {
    try {
      setActionLoading(true);
      const resp = await removeJob(queueName, constentDetails.id);
      if (resp?.success || resp?.status) {
        showSnackbar("Job removed successfully", "success");
        closeConsent();
        fetchData(true);
      }
    } catch (error: any) {
      showSnackbar(error.message || "Failed to remove job", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const getQueueDescription = () => {
    return queueType === "rgs"
      ? "View and manage credit retries, refund retries, and game completion jobs"
      : "View and manage email sending, Slack messages, and export cleanup jobs";
  };

  if (loading) {
    return (
      <ContentShell title={`${QUEUE_LABELS[queueName]} Jobs`}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </ContentShell>
    );
  }

  return (
    <ContentShell title={`${QUEUE_LABELS[queueName]} Jobs`}>
      <Box className={classes.pageContainer}>
        {/* Header */}
        <Box className={classes.headerSection}>
          <Box>
            <Typography className={classes.headerTitle}>
              {QUEUE_LABELS[queueName]}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {getQueueDescription()}
            </Typography>
          </Box>
          <Box className={classes.headerActions}>
            {lastUpdated && (
              <Typography className={classes.lastUpdated}>
                Updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className={classes.refreshButton}
                color="primary"
              >
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Stats Bar */}
        {stats && (
          <Box className={classes.statsBar}>
            {Object.entries(stats).map(([key, value]) => (
              <Box key={key} className={classes.statItem}>
                <Box
                  className={classes.statDot}
                  sx={{
                    backgroundColor:
                      JOB_STATE_COLORS[key as JobState] || "#9e9e9e",
                  }}
                />
                <Typography className={classes.statLabel}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}:
                </Typography>
                <Typography className={classes.statValue}>{value}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Tabs */}
        <Box className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Failed
                  {stats && (
                    <Chip
                      label={stats.failed}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: JOB_STATE_COLORS.failed,
                        color: "#fff",
                      }}
                    />
                  )}
                </Box>
              }
              className={classes.tab}
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Delayed
                  {stats && (
                    <Chip
                      label={stats.delayed}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: JOB_STATE_COLORS.delayed,
                        color: "#fff",
                      }}
                    />
                  )}
                </Box>
              }
              className={classes.tab}
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Waiting
                  {stats && (
                    <Chip
                      label={stats.waiting}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: JOB_STATE_COLORS.waiting,
                        color: "#fff",
                      }}
                    />
                  )}
                </Box>
              }
              className={classes.tab}
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Active
                  {stats && (
                    <Chip
                      label={stats.active}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: JOB_STATE_COLORS.active,
                        color: "#fff",
                      }}
                    />
                  )}
                </Box>
              }
              className={classes.tab}
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Completed
                  {stats && (
                    <Chip
                      label={stats.completed}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: JOB_STATE_COLORS.completed,
                        color: "#fff",
                      }}
                    />
                  )}
                </Box>
              }
              className={classes.tab}
            />
          </Tabs>
        </Box>

        {/* Jobs Table */}
        <Box className={classes.tableContainer}>
          {jobs.length > 0 ? (
            <JobsTable
              jobs={jobs as (FailedJob | DelayedJob)[]}
              queueName={queueName}
              onViewDetails={(job) => handleViewDetails(job)}
              onRetry={
                hasRetryPermission ? (job) => handleRetryJob(job) : undefined
              }
              onRemove={
                hasAdminPermission
                  ? (job) => handleRemoveJobClick(job)
                  : undefined
              }
              showRetry={activeTab === 0} // Only show retry for failed jobs
              showRemove={hasAdminPermission}
            />
          ) : (
            <Box className={classes.noData}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                No {tabStates[activeTab]} jobs
              </Typography>
              <Typography variant="body2">
                There are currently no jobs in the {tabStates[activeTab]} state.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Modals */}
        <JobDetailModal
          open={jobDetailOpen}
          onClose={() => setJobDetailOpen(false)}
          job={selectedJob}
          queueName={queueName}
        />

        <DeleteConsentDialog
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={handleConfirmRemove}
          itemName={constentDetails?.name || constentDetails?.id}
          message="Are you sure you want to remove this job from the queue?"
        />
      </Box>
    </ContentShell>
  );
};

export default QueueJobs;
